// DS: MO3.4
import React, { type HTMLProps } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation, PapiUser, PapiEditorText } from '@wix/da-papi-types';
import DeviationDumbView from '../../../DeviationViews/Dumbo';
import DetailsDumb from '../DetailsDumb';
import UserAvatar from '../../../User/Avatar/UserAvatar';
import UserLink from '../../../User/Link/UserLink';
import BoostFilledIcon from '@wix/da-ds/pkg/Icons/16x16/BoostFilled';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import ShopCardRoot from '../ShopCardRoot';
import s from './ShopCardDumb.scss';

export interface Props {
  className?: string;
  width: number;
  coverImageHeight?: number;
  coverImage?: PapiDeviation | null;
  title?: string;
  withAuthorDetails?: boolean;
  author?: PapiUser;
  withOwnerDetails?: boolean;
  owner?: PapiUser;
  withDescriptionBlock?: boolean;
  description?: PapiEditorText | string | null;
  isBoosted?: boolean;
  indicatorsSlot?: React.ReactNode;
  belowCoverImageSlot?: React.ReactNode;
  detailsRightSlot?: React.ReactNode;
  footerSlot?: React.ReactNode;
  isMultiImage?: boolean;
  'aria-hidden'?: HTMLProps<any>['aria-hidden'];
}

export const COVER_IMG_HEIGHT = 200;
export const OWNER_AVATAR_SIZE = 24;

const ShopCardDumb: React.FC<Props> = ({
  className,
  width,
  coverImage,
  coverImageHeight = COVER_IMG_HEIGHT,
  title,
  withAuthorDetails,
  author,
  withDescriptionBlock,
  description,
  isBoosted,
  indicatorsSlot,
  belowCoverImageSlot,
  detailsRightSlot,
  withOwnerDetails,
  owner,
  footerSlot,
  isMultiImage,
  'aria-hidden': ariaHidden,
}) => {
  const { t } = useTranslation();
  const dumboWidth = width - parseInt(s['border-width']) * 2;

  // todo: there is an edge case where an author -buys back- a work that we
  // may want to handle here at some point
  const isNewOwner = owner && author?.userId && owner.userId !== author.userId;

  return (
    <ShopCardRoot className={className} width={width} aria-hidden={ariaHidden}>
      {isBoosted && !isMultiImage && (
        <>
          <div className={s['top-overlay']} />
          <div className={s['boosted']}>
            <BoostFilledIcon disableDefaultColors size={IconSize.TINY} />
            <div>{t('common.boosted')}</div>
          </div>
        </>
      )}
      <div className={s['indicators-slot']}>{indicatorsSlot}</div>
      <div
        className={s['overlay-container']}
        style={{ width: dumboWidth, height: coverImageHeight }}
      >
        {coverImage ? (
          <DeviationDumbView
            className={s['cover-image']}
            deviation={coverImage}
            useCoverMode
            width={dumboWidth}
            height={coverImageHeight}
            videoIconSize={IconSize.GIANT}
          />
        ) : (
          <div
            style={{ width: dumboWidth, height: coverImageHeight }}
            className={c(s['cover-image'], s['empty-cover-image'])}
            role="presentation"
          />
        )}
        {withOwnerDetails && isNewOwner && (
          <div className={s['owner-overlay']}>
            <div className={s['owner-details']}>
              <UserAvatar user={owner} size={OWNER_AVATAR_SIZE} />
              {t('shop.thumb.adoptable.ownedBy')}
              <UserLink user={owner} className={s['owner-link']} />
            </div>
          </div>
        )}
      </div>
      <div className={s['below-cover-image-slot']}>{belowCoverImageSlot}</div>
      <DetailsDumb
        className={s['details']}
        title={title}
        withAuthorDetails={withAuthorDetails}
        author={author}
        withDescriptionBlock={withDescriptionBlock}
        description={description}
        rightSlot={detailsRightSlot}
        withOwnerDetails={withOwnerDetails}
        owner={owner}
      />
      {footerSlot && <div className={s['footer-slot']}>{footerSlot}</div>}
    </ShopCardRoot>
  );
};
ShopCardDumb.displayName = 'ShopCardDumb';

export default ShopCardDumb;
